import { useEffect, useMemo, useState } from 'react';
import { useAuth } from 'react-oidc-context';

const BANNER_STORAGE_KEY = 'vestra_subscription_renewal_banner_dismissed';
const ONBOARDING_STATUS_STORAGE_KEY = 'vestra_subscription_onboarding_status';
const ONBOARDING_STATUS_CACHE_TTL_MS = 5 * 60 * 1000;

type DismissedBannerMap = Record<string, string>;
type RenewalBannerSelectedApp = 'sr' | 'compliance';
type SignupOnboardingStatusResponse = {
  selected_apps?: string[];
  billing_action_required?: boolean;
  has_billing_portal_access?: boolean;
  can_renew_in_portal?: boolean;
  scheduled_cancellation_at?: string;
};

type CachedOnboardingStatus = {
  cachedAt: number;
  data: SignupOnboardingStatusResponse;
};

type UseSubscriptionRenewalBannerArgs = {
  identityCenterApiUrl: string;
  selectedApp: RenewalBannerSelectedApp;
  storageScope: string;
};

const getCurrentDayKey = () => new Date().toISOString().slice(0, 10);

function readDismissedBannerMap(): DismissedBannerMap {
  const rawValue = localStorage.getItem(BANNER_STORAGE_KEY);
  if (!rawValue) {
    return {};
  }

  try {
    const parsed = JSON.parse(rawValue);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function writeDismissedBannerMap(value: DismissedBannerMap) {
  localStorage.setItem(BANNER_STORAGE_KEY, JSON.stringify(value));
}

function readCachedOnboardingStatus(key: string): SignupOnboardingStatusResponse | null {
  const rawValue = sessionStorage.getItem(key);
  if (!rawValue) {
    return null;
  }

  try {
    const cached = JSON.parse(rawValue) as CachedOnboardingStatus;
    if (
      !cached ||
      typeof cached !== 'object' ||
      typeof cached.cachedAt !== 'number' ||
      !cached.data ||
      Date.now() - cached.cachedAt >= ONBOARDING_STATUS_CACHE_TTL_MS
    ) {
      sessionStorage.removeItem(key);
      return null;
    }

    return cached.data;
  } catch {
    sessionStorage.removeItem(key);
    return null;
  }
}

export const useSubscriptionRenewalBanner = ({
  identityCenterApiUrl,
  selectedApp,
  storageScope,
}: UseSubscriptionRenewalBannerArgs) => {
  const auth = useAuth();
  const identityCenterApiBaseUrl = identityCenterApiUrl.replace(/\/+$/, '');
  const authScopeKey = auth.user?.profile?.sub || auth.user?.access_token || 'anonymous';
  const bannerScopeKey = `${authScopeKey}:${storageScope}:${selectedApp}`;
  const onboardingStatusCacheKey = `${ONBOARDING_STATUS_STORAGE_KEY}:${bannerScopeKey}`;
  const [onboardingStatus, setOnboardingStatus] = useState<SignupOnboardingStatusResponse | null>(
    null,
  );
  const [hasLoadedStatus, setHasLoadedStatus] = useState(false);
  const [loadedBannerScopeKey, setLoadedBannerScopeKey] = useState<string | null>(null);
  const [isProcessingAction, setIsProcessingAction] = useState(false);
  const [isStorageReady, setIsStorageReady] = useState(false);
  const [dismissedBannerMap, setDismissedBannerMap] = useState<DismissedBannerMap>({});

  useEffect(() => {
    setDismissedBannerMap(readDismissedBannerMap());
    setIsStorageReady(true);
  }, []);

  useEffect(() => {
    if (!auth.user?.access_token || !auth.isAuthenticated) {
      setOnboardingStatus(null);
      setLoadedBannerScopeKey(bannerScopeKey);
      setIsProcessingAction(false);
      setHasLoadedStatus(true);
      return;
    }

    let isCancelled = false;
    setHasLoadedStatus(false);
    setLoadedBannerScopeKey(null);
    setOnboardingStatus(null);
    setIsProcessingAction(false);

    const cachedStatus = readCachedOnboardingStatus(onboardingStatusCacheKey);
    if (cachedStatus) {
      setOnboardingStatus(cachedStatus);
      setLoadedBannerScopeKey(bannerScopeKey);
      setHasLoadedStatus(true);
      return;
    }

    const loadOnboardingStatus = async () => {
      try {
        const response = await fetch(
          `${identityCenterApiBaseUrl}/v1/public/signup/onboarding-status`,
          {
            headers: {
              Authorization: `Bearer ${auth.user?.access_token}`,
            },
          },
        );

        if (!response.ok) {
          throw new Error('Failed to load onboarding status');
        }

        const payload = (await response.json()) as SignupOnboardingStatusResponse;

        if (!isCancelled) {
          sessionStorage.setItem(
            onboardingStatusCacheKey,
            JSON.stringify({
              cachedAt: Date.now(),
              data: payload,
            } satisfies CachedOnboardingStatus),
          );
          setOnboardingStatus(payload);
          setLoadedBannerScopeKey(bannerScopeKey);
        }
      } catch (error) {
        console.warn('[subscription-renewal-banner] Failed to load onboarding status:', error);
        if (!isCancelled) {
          setOnboardingStatus(null);
          setLoadedBannerScopeKey(bannerScopeKey);
        }
      } finally {
        if (!isCancelled) {
          setHasLoadedStatus(true);
        }
      }
    };

    void loadOnboardingStatus();

    return () => {
      isCancelled = true;
    };
  }, [
    auth.isAuthenticated,
    auth.user?.access_token,
    bannerScopeKey,
    identityCenterApiBaseUrl,
    onboardingStatusCacheKey,
  ]);

  const canRenewInPortal = Boolean(onboardingStatus?.can_renew_in_portal);
  const hasBillingPortalAccess = Boolean(onboardingStatus?.has_billing_portal_access);
  const isBillingActionRequired = Boolean(onboardingStatus?.billing_action_required);
  const hasSelectedApp = onboardingStatus?.selected_apps?.includes(selectedApp) ?? false;
  const scheduledCancellationAt = onboardingStatus?.scheduled_cancellation_at ?? null;
  const storageKey = `${storageScope}:${selectedApp}`;

  const isVisible = useMemo(() => {
    if (!isStorageReady || !hasLoadedStatus || loadedBannerScopeKey !== bannerScopeKey) {
      return false;
    }

    if (
      !hasSelectedApp ||
      isBillingActionRequired ||
      !canRenewInPortal ||
      !hasBillingPortalAccess
    ) {
      return false;
    }

    return dismissedBannerMap[storageKey] !== getCurrentDayKey();
  }, [
    bannerScopeKey,
    canRenewInPortal,
    dismissedBannerMap,
    hasBillingPortalAccess,
    hasLoadedStatus,
    hasSelectedApp,
    isBillingActionRequired,
    isStorageReady,
    loadedBannerScopeKey,
    storageKey,
  ]);

  const dismiss = () => {
    const nextValue = { ...dismissedBannerMap, [storageKey]: getCurrentDayKey() };
    setDismissedBannerMap(nextValue);
    writeDismissedBannerMap(nextValue);
  };

  const openCustomerPortal = async () => {
    if (!auth.user?.access_token || !canRenewInPortal) {
      return;
    }

    setIsProcessingAction(true);
    try {
      const response = await fetch(
        `${identityCenterApiBaseUrl}/v1/public/signup/billing-portal-session`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth.user.access_token}`,
          },
          body: JSON.stringify({
            return_url: window.location.href,
          }),
        },
      );

      const payload = (await response.json().catch(() => null)) as { portal_url?: string } | null;

      if (!response.ok || !payload?.portal_url) {
        throw new Error('Failed to open customer portal');
      }

      window.location.href = payload.portal_url;
    } catch (error) {
      console.error(
        '[subscription-renewal-banner] Failed to open customer portal for renewal:',
        error,
      );
      setIsProcessingAction(false);
      return;
    }

    setIsProcessingAction(false);
  };

  return {
    isVisible,
    isProcessingAction,
    scheduledCancellationAt,
    dismiss,
    openCustomerPortal,
  };
};
